import React, { Component } from 'react';
import { ThemeContext } from '../../../context/theme.context';
import '../style.css';
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  Legend,
  Category,
  StackingColumnSeries,
                  ColumnSeries,
  Tooltip,
  DataLabel,
  ILoadedEventArgs,
  LineSeries,
} from '@syncfusion/ej2-react-charts';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { getTeamGraph } from '../../../services/KPIsServices/KPIsServices';
import { Scheduler } from '../../../components/GlobalScheduler';
import { BiRefresh, BiArrowBack, BiInfoCircle } from 'react-icons/bi';
import Loader from 'react-spinners/PulseLoader';
import { connect } from 'react-redux';
import type { RootState } from '../../../store';

interface IProps {
  defaultTeams?: number[];
}

type GraphMode = 'trade' | 'tradesperson';
type ViewMode = 'normal' | 'shift';

interface RawRow {
  team_code: string;
  team_id: number | null;
  group_code: string;
  type: 'Shift' | 'Exception';
  shift_code: string;
  color: string;
  Y: number;
}

interface SeriesTicket {
  y: string;
  value: string;
  color: string;
  type: 'Shift' | 'Exception';
}

interface TeamRow {
  team_code: string;
  [seriesKey: string]: any;
}

interface BreakdownRow {
  group_code: string;
  Y: number;
  color: string;
}

interface IState {
  showSpinner: boolean;
  isRefreshing: boolean;
  graphMode: GraphMode;
  viewMode: ViewMode;
  teamData: TeamRow[];
  hiddenTeams: string[];
  drillTeam: string | null;
}

const SHIFT_PALETTE = ['#22d3ee', '#666666', '#7bc96f', '#c96fd6', '#ff6f6f', '#6fd6c9'];
class TeamCapacityGraph extends Component<IProps, IState> {
  static contextType = ThemeContext;
  context!: React.ContextType<typeof ThemeContext>;

  private tickets: SeriesTicket[] = [];
  private rawDataTrade: RawRow[] = [];
  private rawDataPerson: RawRow[] = [];
  private chartObj: ChartComponent;

  constructor(props: IProps) {
    super(props);
    this.state = {
      showSpinner: true,
      isRefreshing: false,
      graphMode: 'trade',
      viewMode: 'normal',
      teamData: [],
      hiddenTeams: [],
      drillTeam: null,
    };
    this.chartObj = new ChartComponent(props);
  }

  async componentDidMount() {
    await this.getTeamsGraph(Scheduler.AllFilterDAta);
  }

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (this.context.isDataChangeKpiTeam) {
      this.context.setDataChangeKpiTeam(false);
      this.getTeamsGraph(Scheduler.AllFilterDAta);
      return;
    }
    if (prevProps.defaultTeams !== this.props.defaultTeams) {  
    this.syncHiddenTeamsFromDefault();
  }
    if (
      prevState.graphMode !== this.state.graphMode ||
      prevState.viewMode !== this.state.viewMode
    ) {
      this.rebuildFromCache();
    }
  }

  onLoad(args: ILoadedEventArgs): void {}

  private teamCodeToId: Map<string, number> = new Map();

private buildTeamCodeIdMap(rows: RawRow[]) {
  rows.forEach((r) => {
    if (r.team_id != null && !this.teamCodeToId.has(r.team_code)) {
      this.teamCodeToId.set(r.team_code, r.team_id);
    }
  });
}

private syncHiddenTeamsFromDefault = () => {
  const defaultTeams = this.props.defaultTeams ?? [];
  if (defaultTeams.length === 0) {
    this.setState({ hiddenTeams: [] });
    return;
  }
  const selectedCodes = new Set<string>();
  this.teamCodeToId.forEach((id, code) => {
    if (defaultTeams.includes(id)) selectedCodes.add(code);
  });
  const allCodes = this.getTeamCodes();
  const hidden = allCodes.filter((code) => !selectedCodes.has(code));
  this.setState({ hiddenTeams: hidden });
};

  private mapRow = (el: any, mode: GraphMode): RawRow => ({
    team_code: el.team_code === '' ? 'NO TEAM' : el.team_code,
    team_id: el.team_id ?? null,
    group_code:
      mode === 'trade'
        ? el.trade_code === ''
          ? 'NO TRADE'
          : el.trade_code
        : !el.element_alias
        ? 'UNKNOWN'
        : el.element_alias,
    type: el.type,
    shift_code: el.shift_code || '',
    color:
      el.type === 'Exception'
        ? '#789395'
        : mode === 'trade'
        ? el.color
        : el.shift_color ?? el.color,
    Y: el?.Y ? Math.abs(el.Y / 60) : 0,
  });

  async getTeamsGraph(query: string, isManualRefresh: boolean = false) {
    if (isManualRefresh) {
      this.setState({ isRefreshing: true });
    } else {
      this.setState({ showSpinner: true });
    }
    await getTeamGraph(query)
      .then((res) => {
        this.rawDataTrade = (res?.data?.dataG1 ?? []).map((el: any) =>
          this.mapRow(el, 'trade')
        );
        this.rawDataPerson = (res?.data?.dataG2 ?? []).map((el: any) =>
          this.mapRow(el, 'tradesperson')
        );
        this.buildTeamCodeIdMap([...this.rawDataTrade, ...this.rawDataPerson]); 
        this.rebuildFromCache();
        this.setState({ showSpinner: false, isRefreshing: false, drillTeam: null }, () => {
          this.syncHiddenTeamsFromDefault();
        });
      })
      .catch((e) => {
        console.log(e);
        this.setState({ showSpinner: false, isRefreshing: false });
      });
  }

  private currentRaw(): RawRow[] {
    return this.state.graphMode === 'trade' ? this.rawDataTrade : this.rawDataPerson;
  }

  private buildPersonColorMap(raw: RawRow[]): Map<string, string> {
    const names = Array.from(
      new Set(raw.filter((r) => r.type !== 'Exception').map((r) => r.group_code))
    ).sort();
    const map = new Map<string, string>();
    names.forEach((name, i) => {
      const hue = (i * 137.508) % 360;
      map.set(name, `hsl(${hue}, 70%, 50%)`);
    });
    return map;
  }

  private personColorMap: Map<string, string> = new Map();

private rebuildFromCache() {
    const raw = this.currentRaw();
    if (this.state.graphMode === 'tradesperson') {
      this.personColorMap = this.buildPersonColorMap(raw);
    }
    if (this.state.viewMode === 'normal') {
      this.tickets = this.buildTickets(raw);
      const teamData = this.buildTeamData(raw, this.tickets);
      this.setState({ teamData });
    } else {
      const { tickets, teamData } = this.buildShiftView(raw);
      this.tickets = tickets;
      this.setState({ teamData });
    }
  }

  private stringToColor(str: string): string {
    if (!str) return '#999999';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 55%)`;
  }

  private buildTickets(raw: RawRow[]): SeriesTicket[] {
    const tickets: SeriesTicket[] = [];
    let counter = 0;
    raw.forEach((row) => {
      const key = row.type === 'Exception' ? 'Exception' : row.group_code;
      const exists = tickets.find((t) => t.value === key && t.type === row.type);
      if (!exists) {
        const color =
          row.type !== 'Exception' && this.state.graphMode === 'tradesperson'
            ? this.personColorMap.get(row.group_code) ?? '#999999'
            : row.color;
        tickets.push({ y: `y${counter}`, value: key, color, type: row.type });
        counter++;
      }
    });
    return tickets;
  }

  private buildTeamData(raw: RawRow[], tickets: SeriesTicket[]): TeamRow[] {
    const rows: TeamRow[] = [];
    raw.forEach((row) => {
      const key = row.type === 'Exception' ? 'Exception' : row.group_code;
      const ticket = tickets.find((t) => t.value === key && t.type === row.type);
      if (!ticket) return;
      let existing = rows.find((r) => r.team_code === row.team_code);
      if (!existing) {
        existing = { team_code: row.team_code };
        rows.push(existing);
      }
      existing[ticket.y] = ((existing[ticket.y] ?? 0) as number) + row.Y;
    });

    rows.forEach((row) => {
      let total = 0;
      tickets.forEach((t) => {
        if (t.type !== 'Exception') total += (row[t.y] as number) || 0;
      });
      row.total = Math.round(total * 10) / 10;
    });

    return rows;
  }

  private buildShiftView(raw: RawRow[]): { tickets: SeriesTicket[]; teamData: TeamRow[] } {
    const shiftCodes = Array.from(
      new Set(
        raw
          .filter((r) => r.type === 'Shift' && r.shift_code)
          .map((r) => r.shift_code)
      )
    ).sort();

    const tickets: SeriesTicket[] = shiftCodes.map((code, i) => ({
      y: `s${i}`,
      value: code,
      color: SHIFT_PALETTE[i % SHIFT_PALETTE.length],
      type: 'Shift',
    }));

    const exceptionTicket: SeriesTicket = {
      y: 'yException',
      value: 'Exception',
      color: '#789395',
      type: 'Exception',
    };
    const hasException = raw.some((r) => r.type === 'Exception');
    if (hasException) tickets.push(exceptionTicket);

    const rows: TeamRow[] = [];
    raw.forEach((row) => {
      let existing = rows.find((r) => r.team_code === row.team_code);
      if (!existing) {
        existing = { team_code: row.team_code };
        rows.push(existing);
      }
      if (row.type === 'Exception') {
        existing[exceptionTicket.y] = ((existing[exceptionTicket.y] ?? 0) as number) + row.Y;
        return;
      }
      const ticket = tickets.find((t) => t.value === row.shift_code && t.type === 'Shift');
      if (!ticket) return;
      existing[ticket.y] = ((existing[ticket.y] ?? 0) as number) + row.Y;
    });

        rows.forEach((row) => {
      let total = 0;
      tickets.forEach((t) => {
        if (t.type !== 'Exception') total += (row[t.y] as number) || 0;
      });
      row.total = Math.round(total * 10) / 10;
    });

    return { tickets, teamData: rows };
  }

  private toggleTeam = (teamCode: string) => {
    this.setState((prev) => {
      const hidden = prev.hiddenTeams.includes(teamCode)
        ? prev.hiddenTeams.filter((t) => t !== teamCode)
        : [...prev.hiddenTeams, teamCode];
      return { hiddenTeams: hidden };
    });
  };

  private getVisibleTeamData(): TeamRow[] {
    return this.state.teamData.filter((row) => !this.state.hiddenTeams.includes(row.team_code));
  }

  private getTeamCodes(): string[] {
    return this.state.teamData.map((r) => r.team_code);
  }

  private onTeamBarClick = (args: any) => {
    const team = args?.point?.x;
    if (team) {
      this.setState({ drillTeam: team });
    }
  };

  private getBreakdownForTeam(teamCode: string): BreakdownRow[] {
    const raw = this.currentRaw();
    const rows: BreakdownRow[] = [];
    raw
      .filter((r) => r.team_code === teamCode && r.type === 'Shift')
      .forEach((r) => {
        let existing = rows.find((x) => x.group_code === r.group_code);
        if (!existing) {
          const color =
            this.state.graphMode === 'tradesperson'
              ? this.personColorMap.get(r.group_code) ?? '#999999'
              : r.color;
          existing = { group_code: r.group_code, Y: 0, color };
          rows.push(existing);
        }
        existing.Y += r.Y;
      });
    return rows;
  }

  private switchGraphMode = (mode: GraphMode) => {
  if (mode === this.state.graphMode) return;
  this.setState({ graphMode: mode, viewMode: 'normal', drillTeam: null }, () => {
    this.rebuildFromCache();
    this.syncHiddenTeamsFromDefault();
  });
};

  private switchViewMode = (mode: ViewMode) => {
    if (mode === this.state.viewMode) return;
    this.setState({ viewMode: mode, drillTeam: null }, () => this.rebuildFromCache());
  };

  private toggleBtnStyle(active: boolean, isDark: boolean): React.CSSProperties {
    return {
      padding: '4px 12px',
      fontSize: '13px',
      fontWeight: 600,
      border: 'none',
      cursor: 'pointer',
      backgroundColor: active ? '#22d3ee' : isDark ? '#2c3a48' : '#e2e2e2',
      color: active ? '#2c3a48' : isDark ? '#EAEAEA' : '#15202B',
      transition: 'background-color 0.3s ease',
    };
  }

  render() {
    const { showSpinner, isRefreshing, drillTeam, graphMode, viewMode } = this.state;
    const isDark = this.context.isDarkMode;

    if (showSpinner) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '300px' }}>
          <Loader color='#22d3ee' />
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '12px',
          borderRadius: '10px',
          backgroundColor: isDark ? '#1c2732' : '#f5f5f5',
          overflowX: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => this.getTeamsGraph(Scheduler.AllFilterDAta, true)}
              disabled={isRefreshing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '2px 8px',
                paddingTop: '4px',
                borderRadius: '6px',
                paddingRight: '12px',
                border: 'none',
                cursor: isRefreshing ? 'default' : 'pointer',
                backgroundColor: isDark ? '#2c3a48' : '#e2e2e2',
                color: isDark ? '#EAEAEA' : '#15202B',
                fontSize: '14px',
                fontWeight: 500,
                opacity: isRefreshing ? 0.6 : 1,
              }}
            >
              <BiRefresh size={24} style={{ paddingBottom: '2px', marginRight: '0px' }} className={isRefreshing ? 'spin-refresh' : ''} />
              <span style={{paddingTop: '1px'}}>Refresh</span>
            </button>

            <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden' }}>
              <button style={this.toggleBtnStyle(graphMode === 'trade', isDark)} onClick={() => this.switchGraphMode('trade')}>
                Team - Trade
              </button>
              <button style={this.toggleBtnStyle(graphMode === 'tradesperson', isDark)} onClick={() => this.switchGraphMode('tradesperson')}>
                Team - Tradesperson
              </button>
            </div>

            <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden' }}>
              <button style={this.toggleBtnStyle(viewMode === 'normal', isDark)} onClick={() => this.switchViewMode('normal')}>
                Default 
              </button>
              <button style={this.toggleBtnStyle(viewMode === 'shift', isDark)} onClick={() => this.switchViewMode('shift')}>
                Shift (DS/NS)
              </button>
            </div>
          </div>

          {!drillTeam ? (
            <div style={{ color: '#ababab' }}>
              <BiInfoCircle style={{ paddingBottom: '2px' }} />
              <span style={{ fontSize: 12, marginLeft: '4px' }}>
                Click a team to view its {graphMode === 'trade' ? 'trades' : 'tradespersons'}
              </span>
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', gap: '16px', minWidth: 0 }}>
          <div
            style={{
              minWidth: '150px',
              maxWidth: '150px',
              maxHeight: '360px',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: isDark ? '#15202B' : '#ffffff',
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Select Team</span>
            {this.getTeamCodes().map((team) => (
              <div key={team} style={{ display: 'flex', alignItems: 'center', margin: '6px 0' }}>
                <CheckBoxComponent checked={!this.state.hiddenTeams.includes(team)} change={() => this.toggleTeam(team)} />
                <span style={{ marginLeft: '6px', fontSize: '12px' }}>{team}</span>
              </div>
            ))}
          </div>

          <div style={{ flex: '1 1 0', minWidth: 0, overflowX: 'auto', marginTop: '-20px', overflowY: 'hidden' }}>
            {drillTeam ? (
              <>
                <div
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginBottom: '6px', paddingTop: '20px', fontSize: '13px' }}
                  onClick={() => this.setState({ drillTeam: null })}
                >
                  <BiArrowBack size={16} />
                  <span style={{ marginLeft: '4px' }}>Return to Teams · {drillTeam}</span>
                </div>
                <ChartComponent
                  id='teamDrillChart'
                  primaryXAxis={{ valueType: 'Category', labelIntersectAction: 'Rotate45', majorGridLines: { width: 0 } }}
                  primaryYAxis={{ labelFormat: '{value}', majorGridLines: { width: 0.5 } }}
                  tooltip={{ enable: true }}
                  width='100%'
                  height='234px'
                  chartArea={{ border: { width: 0 } }}
                  background='transparent'
                >
                  <Inject services={[ColumnSeries, Category, Tooltip, DataLabel]} />
                  <SeriesCollectionDirective>
                    <SeriesDirective
                      dataSource={this.getBreakdownForTeam(drillTeam)}
                      xName='group_code'
                      yName='Y'
                      type='Column'
                      name={drillTeam}
                    />
                  </SeriesCollectionDirective>
                </ChartComponent>
              </>
            ) : (
              <ChartComponent
                id='teamMainChart'
                key={`${graphMode}-${viewMode}`}
                ref={(el) => {
                  this.chartObj = el as ChartComponent;
                }}
                primaryXAxis={{ valueType: 'Category', labelIntersectAction: 'Rotate45', majorGridLines: { width: 0 } }}
                primaryYAxis={{ labelFormat: '{value}', majorGridLines: { width: 0.5 } }}
                tooltip={{ enable: true }}
                pointClick={this.onTeamBarClick}
                loaded={this.onLoad.bind(this)}
                width='100%'
                height='282px'
                chartArea={{ border: { width: 0 } }}
                background='transparent'
                legendSettings={{
                  visible: true,
                  position: 'Top',
                  textStyle: { color: isDark ? '#EAEAEA' : '#15202B', size: '11px' },
                }}
              >
                <Inject services={[StackingColumnSeries, ColumnSeries, LineSeries, Category, Legend, Tooltip, DataLabel]} />
                <SeriesCollectionDirective>
                  {this.tickets.map((ticket) =>
                    ticket.type === 'Exception' ? (
                      <SeriesDirective
                        key={`${graphMode}-${viewMode}-${ticket.value}`}
                        dataSource={this.getVisibleTeamData()}
                        xName='team_code'
                        yName={ticket.y}
                        name={ticket.value}
                        type='Line'
                        fill={ticket.color}
                        marker={{ visible: true, width: 8, height: 8 }}
                        zOrder={1000000}
                      />
                    ) : (
                      <SeriesDirective
                        key={`${graphMode}-${viewMode}-${ticket.value}`}
                        dataSource={this.getVisibleTeamData()}
                        xName='team_code'
                        yName={ticket.y}
                        name={ticket.value}
                        type={viewMode === 'shift' ? 'Column' : 'StackingColumn'}
                        fill={ticket.color}
                        marker={
                          viewMode === 'shift'
                            ? {
                                visible: false,
                                dataLabel: {
                                  visible: true,
                                  position: 'Outer',
                                  font: {
                                    size: '11px',
                                    fontWeight: '600',
                                    color: isDark ? '#EAEAEA' : '#15202B',
                                  },
                                },
                              }
                            : undefined
                        }
                      />
                    )
                  )}
                  {viewMode === 'normal' && this.tickets.length > 0 && (
                    <SeriesDirective
                      key={`${graphMode}-${viewMode}-total`}
                      dataSource={this.getVisibleTeamData()}
                      xName='team_code'
                      yName='total'
                      name='Total'
                      type='Line'
                      width={0}
                      fill='#22d3ee'
                      legendShape='Circle'
                      marker={{
                        visible: false,
                        dataLabel: {
                          visible: true,
                          position: 'Top',
                          font: { size: '12px', fontWeight: '600', color: isDark ? '#EAEAEA' : '#15202B' },
                        },
                      }}
                      zOrder={1000001}
                    />
                  )}
                </SeriesCollectionDirective>
              </ChartComponent>
            )}
          </div>
        </div>
      </div>
    );
  }
}


const mapStateToProps = (state: RootState) => ({
  defaultTeams: state.search.defaultTeams,
});

export default connect(mapStateToProps)(TeamCapacityGraph);